import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector, useOutsideClick } from '../hooks'
import { deleteAccount, updateAuthProfile } from '../store/auth.slice'
import { NavBack } from '../components/nav-back'
import { Spinner } from '../components/spinner'
import { Status } from '../components/status'

export const ProfileView = () => {
  const [account, setAccount] = useState({ name: '' })
  const [isProfileSettingsOpen, setIsProfileSettingsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState({
    updateProfile: false,
    deleteUser: false
  })
  const [requestState, setRequestState] = useState({ success: '', error: '' })
  const inputRef = useRef<HTMLInputElement>(null)
  const profileSettingsTriggerRef = useRef(null)
  const profileSettingsContextRef = useRef(null)

  useOutsideClick({ element: profileSettingsContextRef, trigger: profileSettingsTriggerRef }, () =>
    setIsProfileSettingsOpen(false)
  )

  const dispatch = useAppDispatch()
  const authState = useAppSelector((state) => state.auth)
  const { data: authUser } = authState

  useEffect(() => {
    if (authUser && authUser.name) {
      setAccount({ name: authUser.name })
    }
  }, [authUser])

  const navigate = useNavigate()

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRequestState({ success: '', error: '' })
    setAccount({ ...account, [e.target.id]: e.target.value })
  }

  const trimString = (str: string) => str.trim().replace(/\s+/g, ' ')

  const getIsFieldChanged = (): boolean => {
    if (!authUser) return false

    if (!account.name) {
      return false
    }

    return trimString(account.name) !== trimString(authUser.name ?? '')
  }

  const handleUpdateProfile = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const payload = { ...account }
    payload.name = trimString(payload.name)

    try {
      setIsLoading({ ...isLoading, updateProfile: true })

      if (authUser) {
        const { status } = await dispatch(
          updateAuthProfile({
            id: authUser._id,
            user: payload
          })
        ).unwrap()
        if (status === 200) {
          setRequestState({ success: 'Profile updated', error: '' })
        }
      }
    } catch (error: any) {
      setRequestState({ success: '', error: error.message })
    } finally {
      setIsLoading({ ...isLoading, updateProfile: false })
    }
  }

  const handleDeleteUser = async () => {
    if (window.confirm('This action cannot be reversed. Confirm account deletion?')) {
      if (authUser) {
        try {
          setIsLoading({ ...isLoading, deleteUser: true })

          const { status } = await dispatch(deleteAccount(authUser._id)).unwrap()
          if (status === 200) {
            navigate('/', { viewTransition: true })
          }
        } catch (error: any) {
          setRequestState({ success: '', error: error.message })
          setIsProfileSettingsOpen(false)
        } finally {
          setIsLoading({ ...isLoading, deleteUser: false })
        }
      }
    } else {
      setIsProfileSettingsOpen(false)
    }
  }

  return (
    <>
      {isLoading.deleteUser ? (
        <Spinner size='center' />
      ) : (
        <>
          <div className='main_content'>
            <NavBack />
            {requestState.success && (
              <Status
                success={requestState.success}
                reset={() => setRequestState({ ...requestState, success: '' })}
              />
            )}
            {requestState.error && (
              <Status
                error={requestState.error}
                reset={() => setRequestState({ ...requestState, error: '' })}
              />
            )}
            <div className='profile_content'>
              <h1 className='heading-styled'>My account</h1>
              {authUser && (
                <form onSubmit={handleUpdateProfile} className='form-grid'>
                  <label htmlFor='name'>Name:</label>
                  <div className='input_container'>
                    <input
                      id='name'
                      value={account.name}
                      onChange={handleChange}
                      ref={inputRef}
                      autoComplete='off'
                      placeholder='Enter name...'
                    />
                    <span
                      className='material-symbols-rounded'
                      onClick={() => inputRef.current?.focus()}>
                      edit
                    </span>
                  </div>
                  <label htmlFor='email'>Email:</label>
                  <div className='input_container-subtext'>
                    <input id='email' value={authUser.email} readOnly />
                    <span className='subtext subtext-sm subtext-thin'>
                      <span className='material-symbols-rounded'>shield_lock</span>
                      <span>Your email has been verified.</span>
                    </span>
                  </div>
                  {getIsFieldChanged() && (
                    <button className='submit' disabled={isLoading.updateProfile}>
                      {isLoading.updateProfile ? <Spinner /> : <span>Save changes</span>}
                    </button>
                  )}
                </form>
              )}
              <span
                className='span-a'
                onClick={() => navigate('/reset-password', { viewTransition: true })}>
                Update password
              </span>
            </div>
          </div>
          <div className='footer_actions footer_actions-alt'>
            <span
              id='profile_settings_trigger'
              className='material-symbols-rounded'
              onClick={() => setIsProfileSettingsOpen(!isProfileSettingsOpen)}
              ref={profileSettingsTriggerRef}>
              settings
            </span>
            {isProfileSettingsOpen && (
              <div className='context_menu context_menu-up' ref={profileSettingsContextRef}>
                <div className='div-with-icon error' onClick={handleDeleteUser}>
                  <span className='material-symbols-rounded'>delete</span>
                  <span>Delete my account</span>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </>
  )
}
